import { FC } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { SopItem, SopMes } from '@/types/dashboards';
import type { ModoTopDesvio, TopDesvio } from '@/utils/equiplexSop';
import type { MedidasRotulo } from '@/utils/rotulosGrafico';
import { fmtCompacto, fmtDesvio, fmtUn, mesCurto } from '@/utils/painelFormat';
import AreaDoGrafico from '../AreaDoGrafico';
import { ALTURA_BARRA_HORIZONTAL, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import { rotuloNaPonta } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';

interface Props {
  top: TopDesvio[];
  modo: ModoTopDesvio;
  onModo: (modo: ModoTopDesvio) => void;
  itens: Record<string, SopItem>;
  meses: Record<string, SopMes>;
  cores: CoresPainel;
}

type Ponto = TopDesvio & { rotulo: string; valor: number };

const sinal = (v: number): string => (v > 0 ? '+' : v < 0 ? '−' : '');

/** "+12 mil" / "−8 mil": a diferença em unidades, compacta e com sinal. */
const fmtGap = (v: number): string => `${sinal(v)}${fmtCompacto(Math.abs(v))}`;

/** Folga de 30% nas pontas: o número da barra mais comprida não invade os nomes nem sai do gráfico. */
const folga = (lado: 'min' | 'max') => (v: number): number => (lado === 'min' ? Math.min(0, v * 1.3) : Math.max(0, v * 1.3));

/**
 * Barra horizontal: o número precisa de ~300 px de área de desenho além dos nomes do SKU (152 do
 * eixo + margens). Sem modo em pé e sem rolar: rolando, as barras sumiriam da tela junto com o
 * número. No celular o número fica no tooltip, como antes (03/11/2026).
 */
const MEDIDAS: MedidasRotulo = { reservado: 176, deitado: 300 };

/** Top 10 SKU×mês mais longe do programado — barras divergentes a partir do zero, com o número na ponta. */
const TopDesviosChart: FC<Props> = ({ top, modo, onModo, itens, meses, cores }) => {
  const dados: Ponto[] = top.map((t) => ({
    ...t,
    rotulo: `${t.codigoDatasul} · ${mesCurto(meses[t.mes]?.label ?? t.mes)}`,
    valor: modo === 'unidades' ? t.gap : t.desvioPct,
  }));

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as Ponto) : null;
    if (!p) return null;
    return (
      <TooltipBox
        titulo={
          <>
            {p.codigoDatasul} · {meses[p.mes]?.label ?? p.mes}
            <div className="font-normal text-content-muted">{itens[p.codigoDatasul]?.descricao ?? 'Item sem cadastro'}</div>
          </>
        }
        linhas={[
          { rotulo: 'Programado', valor: fmtUn(p.programadoBase) },
          { rotulo: 'Produzido', valor: fmtUn(p.produzido) },
          { rotulo: 'Diferença', valor: `${sinal(p.gap)}${fmtUn(Math.abs(p.gap))}` },
          { rotulo: 'Desvio', valor: fmtDesvio(p.desvioPct) },
        ]}
      />
    );
  };

  const alternador = (
    <div role="group" aria-label="Ordenar desvios por" className="inline-flex rounded-lg border border-theme-border p-0.5 text-xs">
      {(['unidades', 'percentual'] as const).map((m) => (
        <button
          key={m}
          type="button"
          aria-pressed={modo === m}
          onClick={() => onModo(m)}
          className={`rounded-md px-2.5 py-1 ${modo === m ? 'bg-surface-hover text-content-primary font-medium' : 'text-content-muted hover:text-content-primary'}`}
        >
          {m === 'unidades' ? 'Em unidades' : 'Em %'}
        </button>
      ))}
    </div>
  );

  return (
    <PainelCard
      testId="sop-chart-top-desvios"
      titulo="Maiores desvios por SKU"
      subtitulo={
        modo === 'unidades'
          ? 'Os dez SKU×mês com maior diferença absoluta entre produzido e programado.'
          : 'Os dez SKU×mês com maior desvio percentual — OPs pequenas pesam tanto quanto as grandes.'
      }
      acoes={alternador}
    >
      <Legenda
        itens={[
          { rotulo: 'Produziu acima do programado', cor: cores.acima },
          { rotulo: 'Produziu abaixo do programado', cor: cores.abaixo },
        ]}
      />
      {dados.length === 0 ? (
        <div className="py-10 text-center text-sm text-content-muted">Nenhum SKU com programado no recorte.</div>
      ) : (
        <AreaDoGrafico altura={Math.max(200, dados.length * ALTURA_BARRA_HORIZONTAL + 30)} categorias={1} medidas={MEDIDAS} semRolagem>
          {(rotulo) => (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dados} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 8 }} barCategoryGap={8}>
                {/* O número está na ponta de cada barra: a régua de valores sobra e sai. */}
                <XAxis type="number" hide domain={[folga('min'), folga('max')]} />
                <YAxis
                  type="category"
                  dataKey="rotulo"
                  width={152}
                  tick={tickEixo(cores)}
                  axisLine={false}
                  tickLine={false}
                />
                <ReferenceLine x={0} stroke={cores.eixo} />
                <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
                <Bar dataKey="valor" radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
                  {dados.map((d) => (
                    <Cell key={d.chave} fill={d.valor >= 0 ? cores.acima : cores.abaixo} />
                  ))}
                  {rotulo && <LabelList dataKey="valor" content={rotuloNaPonta(modo === 'unidades' ? fmtGap : fmtDesvio, cores.texto)} />}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </AreaDoGrafico>
      )}
    </PainelCard>
  );
};

export default TopDesviosChart;
